"use client";

import { useState } from "react";
import SoftHero from "./SoftHero";
import SoftCategories from "./SoftCategories";
import SoftProducts from "./SoftProducts";
import SoftDeals from "./SoftDeals";
import { SoftCartProvider } from "./SoftCartContext";
import SoftCartDrawer from "./SoftCartDrawer";
import Image from "next/image";
import Link from "next/link";
import {
  Truck,
  ShieldCheck,
  RotateCcw,
  Headphones,
  Star,
  CheckCircle2,
  Calendar,
  ChevronDown,
  ArrowRight,
} from "lucide-react";

interface SoftStorefrontProps {
  subdomain: string;
  settings: any;
  products?: any[];
  categories?: any[];
  banners?: any[];
  testimonials?: any[];
  blogs?: any[];
  faqs?: any[];
}

function SoftStoreContent({
  subdomain,
  settings,
  products = [],
  categories = [],
  banners = [],
  testimonials = [],
  blogs = [],
  faqs = [],
}: SoftStorefrontProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const resolveImg = (url: string | null | undefined, fallback: string) => {
    if (!url) return fallback;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `http://${subdomain}.localhost:8000${cleanPath}`;
  };

  const filteredProducts = products.filter((p) => {
    const matchSearch = (p.name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat =
      activeCategory === "all" ||
      String(p.category?.id || p.category) === String(activeCategory);
    return matchSearch && matchCat;
  });

  const featured = filteredProducts.filter((p) => p.is_featured);
  const displayFeatured = featured.length > 0 ? featured : filteredProducts;
  const bestsellers = filteredProducts.filter((p) => p.is_bestseller);
  const countdownBanner = banners.find((b) => b.banner_type === "countdown") || null;
  const deals = filteredProducts.filter((p) => Boolean(p.is_deal_of_day));

  return (
    <div className="space-y-12 pb-24">
      <SoftHero
        settings={settings}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      <div id="categories">
        <SoftCategories
          categories={categories}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          subdomain={subdomain}
        />
      </div>

      <div id="featured">
        <SoftProducts
          title="Featured Classified Listings"
          badgeText="Verified Store Items"
          products={displayFeatured}
          subdomain={subdomain}
        />
      </div>

      {countdownBanner && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 rounded-[36px] p-8 sm:p-12 text-white shadow-2xl shadow-blue-500/20 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center md:text-left">
              <span className="px-3 py-1 rounded-full bg-white/20 text-white text-[10px] font-black uppercase tracking-wider border border-white/30 inline-block">
                Limited Time Promo
              </span>
              <h3 className="text-2xl sm:text-4xl font-black">{countdownBanner.title}</h3>
              <p className="text-blue-100 text-xs sm:text-sm max-w-md">
                {countdownBanner.subtitle || "Save up to 35% on seasonal items & verified essentials."}
              </p>
            </div>
            <Link
              href={countdownBanner.target_url || "/products"}
              className="px-8 py-3.5 rounded-full bg-white text-blue-700 hover:bg-blue-50 text-xs font-black tracking-wide shadow-xl transition-all inline-flex items-center gap-2"
            >
              <span>{countdownBanner.button_text || "Grab Offer Now"}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      <SoftDeals deals={deals} subdomain={subdomain} resolveImg={resolveImg} />

      {bestsellers.length > 0 && (
        <SoftProducts
          title="Best Seller Products"
          badgeText="Customer Favorites"
          products={bestsellers}
          subdomain={subdomain}
        />
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Truck, title: "Super Fast Delivery", desc: "Doorstep drop in 24 hrs" },
            { icon: ShieldCheck, title: "100% Authentic", desc: "Verified merchant goods" },
            { icon: RotateCcw, title: "Instant Returns", desc: "Hassle-free 7 day replacement" },
            { icon: Headphones, title: "24/7 Dedicated Support", desc: "Call or chat anytime" },
          ].map((badge, idx) => {
            const Icon = badge.icon;
            return (
              <div
                key={idx}
                className="bg-white rounded-3xl p-5 shadow-[6px_6px_14px_#dce5ef,-6px_-6px_14px_#ffffff] border border-white flex items-center gap-3.5"
              >
                <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-slate-900">{badge.title}</h4>
                  <p className="text-[10px] text-slate-400 mt-0.5">{badge.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-slate-900">What Customers Say</h2>
            <p className="text-xs text-slate-400 mt-1">Real reviews from verified shoppers</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t: any) => (
              <div
                key={t.id}
                className="bg-white rounded-3xl p-6 shadow-[8px_8px_18px_#dce5ef,-8px_-8px_18px_#ffffff] border border-white space-y-3"
              >
                <div className="flex items-center gap-1 text-amber-400">
                  {[...Array(t.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400" />
                  ))}
                </div>
                <p className="text-xs text-slate-600 leading-relaxed italic">
                  &ldquo;{t.comment}&rdquo;
                </p>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-slate-900">{t.customer_name}</h4>
                    <span className="text-[10px] text-slate-400">{t.designation || "Shopper"}</span>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {blogs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-slate-900">Stories & Updates</h2>
            <p className="text-xs text-slate-400 mt-1">Tips, shopping tricks and lifestyle blogs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogs.slice(0, 3).map((post: any) => (
              <div
                key={post.id}
                className="bg-white rounded-3xl p-4 shadow-[8px_8px_18px_#dce5ef,-6px_-6px_18px_#ffffff] border border-white flex flex-col justify-between"
              >
                <div>
                  <div className="relative w-full h-44 rounded-2xl overflow-hidden mb-3">
                    <Image
                      src={resolveImg(post.cover_image_url || post.cover_image, "https://images.unsplash.com/photo-1542838132-92c53300491e?w=500")}
                      alt={post.title}
                      fill
                      unoptimized
                      className="object-cover"
                    />
                  </div>
                  <span className="text-[10px] font-bold text-blue-600 uppercase">
                    {post.category_tag || "General"}
                  </span>
                  <h3 className="text-sm font-black text-slate-900 mt-1 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{post.summary}</p>
                </div>
                <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{post.published_at ? String(post.published_at).slice(0, 10) : "Recent"}</span>
                  </span>
                  <span className="font-bold text-blue-600 hover:underline cursor-pointer">Read →</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section id="faq" className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-slate-900">Frequently Asked Questions</h2>
            <p className="text-xs text-slate-400 mt-1">Everything you need to know about deliveries and orders</p>
          </div>
          <div className="space-y-3.5">
            {faqs.map((faq: any) => {
              const isOpen = activeFaq === faq.id;
              return (
                <div
                  key={faq.id}
                  className="bg-white rounded-2xl shadow-[6px_6px_14px_#dce5ef,-6px_-6px_14px_#ffffff] border border-white overflow-hidden transition-all"
                >
                  <button
                    type="button"
                    onClick={() => setActiveFaq(isOpen ? null : faq.id)}
                    className="w-full p-4 text-left flex items-center justify-between gap-4 cursor-pointer"
                  >
                    <span className="text-xs sm:text-sm font-bold text-slate-800">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-slate-400 transition-transform ${
                        isOpen ? "rotate-180 text-blue-600" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs text-slate-500 leading-relaxed border-t border-slate-50">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-4 pt-4">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[36px] p-8 sm:p-12 text-center text-white shadow-2xl shadow-blue-500/25 space-y-4">
          <h3 className="text-2xl sm:text-3xl font-black">
            Stay Updated with {settings?.store_name || subdomain}
          </h3>
          <p className="text-blue-100 text-xs sm:text-sm max-w-md mx-auto">
            Subscribe to our weekly alerts and get special discount coupons on your next order.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              alert("Subscribed successfully!");
            }}
            className="max-w-md mx-auto flex items-center bg-white rounded-full p-1.5 shadow-xl"
          >
            <input
              type="email"
              required
              placeholder="Enter your email..."
              className="flex-1 px-4 text-xs text-slate-800 outline-none"
            />
            <button
              type="submit"
              className="px-6 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all cursor-pointer"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default function SoftClassifiedStorefront(props: SoftStorefrontProps) {
  return (
    <SoftCartProvider subdomain={props.subdomain}>
      <SoftStoreContent {...props} />
      <SoftCartDrawer subdomain={props.subdomain} />
    </SoftCartProvider>
  );
}